import React, { useState, useEffect, useRef } from "react";
import { Worker, Viewer } from "@react-pdf-viewer/core";
// import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { ReactComponent as CloseIcon } from "../../../../Assets/Images/Close-icon.svg";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import VybogTALLoaderAnimation from "../../../../Common/Dynamic-Animations/DynamicAnimations.js";
import Button from "../../../../Common/Button/Button.js";
import CustomTooltip from "../../../../Common/Tooltip/Tooltip.js";

const IMAGE_EXTENSIONS = [
  "jpg",
  "jpeg",
  "jfif",
  "png",
  "gif",
  "bmp",
  "webp",
  "svg",
  "avif",
  "ico",
  "heic",
  "heif",
  "tif",
  "tiff",
];

const RefreshIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M23 4v6h-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1 20v-6h6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ResumeViewerModal = ({ url, name, onClose }) => {
  // const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const getPdfJsVersion = () => {
    try {
      const pdfjsDist = require("pdfjs-dist/package.json");
      return pdfjsDist.version;
    } catch (error) {
      console.error("Getting 3.11.174 Static version");
      return "3.11.174";
    }
  };
  const workerUrl = `//unpkg.com/pdfjs-dist@${getPdfJsVersion()}/build/pdf.worker.min.js`;
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [textContent, setTextContent] = useState("");
  const [isZoomed, setIsZoomed] = useState(false);
  const iframeRef = useRef(null);

  const lowerName = name?.toLowerCase() || "";
  const fileExtension = lowerName.split(".").pop();
  const isPdf = lowerName.endsWith(".pdf");
  const isDoc = lowerName.endsWith(".doc") || lowerName.endsWith(".docx");
  const isTxt = lowerName.endsWith(".txt");
  const isImage = IMAGE_EXTENSIONS.includes(fileExtension);

  const handlePdfError = () => {
    setError(
      "Failed to load the PDF file. This could be due to an invalid file, network issues, or an unsupported format. Please check the file URL or try a different file.",
    );
    setIsLoading(false);
  };
  const handleDocError = () => {
    setError(
      "Failed to load the document. This could be due to an invalid file, network issues, or an unsupported format. Please ensure the file is accessible or try a different file.",
    );
    setIsLoading(false);
  };

  const handleTxtError = () => {
    setError(
      "Failed to load the text file. This could be due to an invalid file, network issues, or an unsupported format. Please ensure the file is accessible or try a different file.",
    );
    setIsLoading(false);
  };

  const handleImageError = () => {
    setError(
      "Failed to load the image. The link may have expired, or this image format (for example HEIC or TIFF) cannot be previewed in the browser.",
    );
    setIsLoading(false);
  };

  const handleRefresh = () => {
    if (isDoc && iframeRef.current) {
      iframeRef.current.src = `${url}`;
    }
  };

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    setIsZoomed(false);

    if (isDoc && iframeRef.current) {
      iframeRef.current.src = `${url}`;
    }

    if (isTxt && url) {
      fetch(url)
        .then((response) => {
          if (!response.ok) {
            throw new Error("Network response was not ok");
          }
          return response.text();
        })
        .then((text) => {
          setTextContent(text);
          setIsLoading(false);
        })
        .catch(handleTxtError);
    }
  }, [url, isDoc, isTxt, isImage]);

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <CloseIcon onClick={onClose} style={closeButtonStyle} />
        {isDoc && (
          <div
            style={{
              position: "absolute",
              top: "4px",
              right: "30px",
              zIndex: 1100,
            }}
          >
            <CustomTooltip
              title="Refresh if the document takes too long to load."
              placement="bottom"
            >
              <Button
                event={handleRefresh}
                style={{ ...refreshButtonStyle, position: "static" }}
                icon={<RefreshIcon />}
              />
            </CustomTooltip>
          </div>
        )}
        {error ? (
          <div style={errorStyle}>
            <h3>Error Loading File</h3>
            <p>{error}</p>
            <button onClick={onClose} style={retryButtonStyle}>
              Close
            </button>
          </div>
        ) : isPdf ? (
          <Worker workerUrl={workerUrl}>
            <Viewer
              fileUrl={url}
              onDocumentLoadError={handlePdfError}
              // plugins={[defaultLayoutPluginInstance]}
              onLoadSuccess={() => setIsLoading(false)}
            />
          </Worker>
        ) : isImage ? (
          <div
            style={{
              width: "100%",
              height: "100%",
              position: "relative",
              overflow: "auto",
              display: "flex",
              alignItems: isZoomed ? "flex-start" : "center",
              justifyContent: isZoomed ? "flex-start" : "center",
              background: "#f5f5f5",
            }}
          >
            {isLoading && (
              <div style={loadingStyle}>
                <VybogTALLoaderAnimation />
              </div>
            )}
            <img
              src={url}
              alt={name || "Document"}
              onLoad={() => setIsLoading(false)}
              onError={handleImageError}
              onClick={() => setIsZoomed((prev) => !prev)}
              title={isZoomed ? "Click to fit to screen" : "Click to zoom"}
              style={
                isZoomed
                  ? {
                      maxWidth: "none",
                      maxHeight: "none",
                      cursor: "zoom-out",
                      display: isLoading ? "none" : "block",
                    }
                  : {
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      cursor: "zoom-in",
                      display: isLoading ? "none" : "block",
                    }
              }
            />
          </div>
        ) : isDoc ? (
          <div style={{ width: "100%", height: "100%", position: "relative" }}>
            {isLoading && (
              <div style={loadingStyle}>
                <VybogTALLoaderAnimation />
              </div>
            )}
            <iframe
              ref={iframeRef}
              src={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
                url,
              )}`}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                marginTop: "20px",
                background: "white",
              }}
              title="Document Viewer"
              allowFullScreen
              onError={handleDocError}
              onLoad={() => setIsLoading(false)}
            />
          </div>
        ) : isTxt ? (
          <div style={{ width: "100%", height: "100%", position: "relative" }}>
            {isLoading && (
              <div style={loadingStyle}>
                <VybogTALLoaderAnimation />
              </div>
            )}
            <pre
              style={{
                height: "100%",
                margin: 0,
                overflowY: "auto",
                padding: "10px",
                backgroundColor: "#fff",
                color: "#000",
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
                fontFamily: "inherit",
              }}
            >
              {textContent}
            </pre>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "20px" }}>
            Unsupported file format: {name}
          </div>
        )}
      </div>
    </div>
  );
};

const modalStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.8)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};

const modalContentStyle = {
  position: "relative",
  backgroundColor: "#fff",
  width: "80%",
  height: "90%",
  borderRadius: "8px",
  padding: "16px",
  overflow: "hidden",
  boxShadow: "0 0 10px rgba(0,0,0,0.5)",
};

const closeButtonStyle = {
  position: "absolute",
  top: "6px",
  right: "6px",
  cursor: "pointer",
  zIndex: "1100",
};

const errorStyle = {
  textAlign: "center",
  padding: "20px",
  color: "#d32f2f",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
};

const retryButtonStyle = {
  marginTop: "10px",
  padding: "10px 20px",
  backgroundColor: "#1976d2",
  color: "white",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
};

const refreshButtonStyle = {
  position: "absolute",
  top: "10px",
  right: "50px",
  zIndex: 1000,
  padding: "6px 12px",
  backgroundColor: "#fff",
  color: "black",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "500",
};

const loadingStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  color: "#1976d2",
  fontSize: "18px",
};

export default ResumeViewerModal;